import React, { useEffect, useLayoutEffect, useMemo } from "react";
import { TransformControls } from "@react-three/drei";
import {
  ThreeEvent,
  createPortal,
  useFrame,
  useThree,
} from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import {
  DRACOLoader,
  GLTFLoader,
  KTX2Loader,
  MeshoptDecoder,
  TransformControls as TransformControlsImpl,
} from "three-stdlib";
import {
  SymbolType,
  WallType,
  isSvgType,
} from "../../../../../types/floorplannerTypes";
import { editorStoreContext } from "../../../../../store/editorStore";
import { FloorplannerStoreContext } from "../../../../../store/floorplannerStore";
import { getSymbol3DPlanScale } from "../../../../../utils/symbolFlipState";
import { isSymbolAspectRatioLocked } from "../../../../../utils/symbolAspectRatio";
import {
  hasAssignedSlotMaterial,
  resolvePrimitiveSymbolFillColor,
  resolvePrimitiveSymbolPartColor,
  resolvePrimitiveSymbolPartMaterial,
  resolvePrimitiveSymbolPartSurfaceRef,
  resolveSymbolPattern,
  shouldUseSymbolFillColorIn3D,
} from "../../../materials/primitiveSymbolFill";
import type { FloorPlan3DMaterialSlot } from "../../../materials/materialResolver";
import { clampFillOpacity, resolveFillOpacity } from "../../../../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../../../../utils/opacityRemount";
import CatalogStandardMaterial from "../../../materials/CatalogStandardMaterial";
import LitPatternMaterial from "../../../materials/LitPatternMaterial";
import { loadCatalogTexture } from "../../../materials/textureCache";
import { useBuiltInMaterialSurfaceMappingsRevision } from "../../../materials/builtInMaterialSurfaceMappings";
import { trackFloorplan3DMetric } from "../../../metrics/floorplan3DMetrics";
import { useSelectedSymbol3DDrag } from "../../../interaction/useSelectedSymbol3DDrag";
import type { Symbol3DDragVisualState } from "../../../interaction/useSelectedSymbol3DDrag";
import { getSymbol3DGroupTranslateOffset } from "../../../interaction/symbol3DGroupTranslate";
import {
  applyRotationGizmoSensitivity,
  createRotationGizmoSensitivitySession,
  getRotationGizmoControlSize,
  getSymbol3DRotationFromObject,
  isPointerOverTransformControlsPicker,
  positiveDimension,
  registerTransformControlsPointerGuard,
  ROTATION_GIZMO_SNAP_STEP,
  syncSymbolPlanRotationAfter3DYawChange,
  type RotationGizmoSensitivitySession,
  type Symbol3DRotation,
} from "../../../interaction/symbol3DRotationGizmo";
import SelectedMeshOutline from "../../../scene/SelectedMeshOutline";
import { usePointerUpSelection } from "../../../scene/usePointerUpSelection";
import {
  ResolvedSymbol3DAsset,
  resolveSymbol3DAsset,
} from "../../assets/symbol3DAssetResolver";
import {
  computeFloorplannerAssetTransform,
  getSymbol3DAssetFitMode,
  getSymbol3DAssetScaleComponents,
} from "../../assets/symbol3DAssetTransform";
import {
  isMissingSymbol3DAssetLoadError,
  isSymbol3DAssetUnavailable,
  markSymbol3DAssetUnavailable,
} from "../../assets/symbol3DAssetAvailability";
import {
  createSymbolAssetFootprintSvg,
  createSymbolFootprintOutlineSvg,
  getSymbolPlanFootprint,
  isUploadedModel3DSymbol,
  tiltsModelOutOfPlan,
} from "../../assets/userModelFootprintSvg";
import {
  LivingRoom3DPart,
  createLivingRoomSymbol3DGeometry,
} from "./livingRoomGeometry";
import {
  applyLoungeChair1BackrestProfile,
  createLoungeChair1ShellBufferGeometry,
} from "./loungeChair1Geometry";
import {
  createPillowBufferGeometry,
  createSoftBoxBufferGeometry,
} from "./loungeChairPillowGeometry";
import {
  applyLoungeChair2WrapperTopSlope,
  createLoungeChair2ExtrudedGeometry,
  createLoungeChair2ShellBufferGeometry,
} from "./loungeChair2Geometry";
import { createSlopedBasinBufferGeometry } from "./slopedBasinGeometry";
import {
  createBonnieProfileBufferGeometry,
  createBonnieTorsoBufferGeometry,
} from "./bonnieGeometry";

const noopRaycast: THREE.Object3D["raycast"] = () => undefined;
const meshRaycast = THREE.Mesh.prototype.raycast;
type LoadedSymbolGLTF = { scene: THREE.Object3D };
const DRACO_DECODER_PATH =
  "https://www.gstatic.com/draco/versioned/decoders/1.5.5/";
const symbolAssetLoadCache = new Map<
  string,
  | { status: "loading"; promise: Promise<LoadedSymbolGLTF> }
  | { status: "loaded"; gltf: LoadedSymbolGLTF }
>();
let sharedDracoLoader: DRACOLoader | null = null;

const LivingRoomPrimitive3DMeshes: React.FC<{
  symbols: SymbolType[];
  walls: WallType[];
  selectedSymbolIds: Set<string>;
  assetRenderingEnabled?: boolean;
  claimSelectionEvents?: boolean;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onDragActiveChange?: (active: boolean) => void;
  onAssetLoadingChange?: (symbolId: string, loading: boolean) => void;
  persistThreeDOnDrag?: boolean;
}> = observer(
  ({
    symbols,
    walls,
    selectedSymbolIds,
    assetRenderingEnabled = false,
    claimSelectionEvents = false,
    onSelectSymbol,
    onDragActiveChange,
    onAssetLoadingChange,
    persistThreeDOnDrag = true,
  }) => (
    <>
      {symbols.map((symbol) => (
        <PrimitiveSymbol
          key={symbol.id}
          symbol={symbol}
          walls={walls}
          selected={selectedSymbolIds.has(symbol.id)}
          assetRenderingEnabled={assetRenderingEnabled}
          claimSelectionEvents={claimSelectionEvents}
          onSelectSymbol={onSelectSymbol}
          onDragActiveChange={onDragActiveChange}
          onAssetLoadingChange={onAssetLoadingChange}
          persistThreeDOnDrag={persistThreeDOnDrag}
        />
      ))}
    </>
  ),
);

const PrimitiveSymbol: React.FC<{
  symbol: SymbolType;
  walls: WallType[];
  selected: boolean;
  assetRenderingEnabled?: boolean;
  claimSelectionEvents?: boolean;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onDragActiveChange?: (active: boolean) => void;
  onAssetLoadingChange?: (symbolId: string, loading: boolean) => void;
  persistThreeDOnDrag?: boolean;
}> = observer(
  ({
    symbol,
    walls,
    selected,
    assetRenderingEnabled = false,
    claimSelectionEvents = false,
    onSelectSymbol,
    onDragActiveChange,
    onAssetLoadingChange,
    persistThreeDOnDrag = true,
  }) => {
    const editorStore = React.useContext(editorStoreContext);
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { camera, invalidate, scene } = useThree();
    const groupRef = React.useRef<THREE.Group | null>(null);
    const primitiveRotationGroupRef = React.useRef<THREE.Group | null>(null);
    const primitiveRotationWorldPositionRef = React.useRef(new THREE.Vector3());
    const primitiveTransformControlsRef =
      React.useRef<TransformControlsImpl | null>(null);
    const primitiveRotateTransactionRef = React.useRef(false);
    const primitiveRotateChangedRef = React.useRef(false);
    const primitiveRotationStart3DRef = React.useRef<
      Symbol3DRotation | undefined
    >(undefined);
    const primitiveRotationSensitivityRef =
      React.useRef<RotationGizmoSensitivitySession | null>(null);
    const [isPrimitiveRotationDragging, setIsPrimitiveRotationDragging] =
      React.useState(false);
    const [primitiveRotationObject, setPrimitiveRotationObject] =
      React.useState<THREE.Group | null>(null);
    const geometry = useMemo(
      () => createLivingRoomSymbol3DGeometry(symbol),
      [
        symbol,
        symbol.libraryKey,
        symbol.threeD?.modelKey,
        symbol.threeD?.height,
        symbol.threeD?.baseElevation,
        "bookShelf" in symbol ? symbol.bookShelf : undefined,
        "svgLength" in symbol ? symbol.svgLength : undefined,
        "svgHeight" in symbol ? symbol.svgHeight : undefined,
        "polygonWidth" in symbol ? symbol.polygonWidth : undefined,
        "polygonHeight" in symbol ? symbol.polygonHeight : undefined,
      ],
    );
    const selectionHandlers = usePointerUpSelection(symbol, onSelectSymbol, {
      claimPointerEvents: claimSelectionEvents,
    });
    const dragHandlers = useSelectedSymbol3DDrag({
      symbol,
      selected,
      walls,
      onDragActiveChange,
      visualTargetRef: groupRef,
      applyVisualState: applyLooseSymbolVisualState,
      persistThreeDOnDrag,
    });
    const selectedForRotationControls = Boolean(
      selected &&
        editorStore.selections.length === 1 &&
        editorStore.selections[0]?.id === symbol.id,
    );
    const pointerHandlers = useMemo(
      () => ({
        onPointerDown: (event: ThreeEvent<PointerEvent>) => {
          if (
            isPointerOverTransformControlsPicker(
              primitiveTransformControlsRef.current,
              event,
            )
          ) {
            return;
          }
          dragHandlers.onPointerDown(event);
          selectionHandlers.onPointerDown(event);
        },
        onPointerMove: (event: ThreeEvent<PointerEvent>) => {
          dragHandlers.onPointerMove(event);
          selectionHandlers.onPointerMove(event);
        },
        onPointerUp: (event: ThreeEvent<PointerEvent>) => {
          dragHandlers.onPointerUp(event);
          selectionHandlers.onPointerUp(event);
        },
        onPointerCancel: (event: ThreeEvent<PointerEvent>) => {
          dragHandlers.onPointerCancel(event);
        },
      }),
      [dragHandlers, selectionHandlers],
    );
    const assetResolution = useMemo(
      () =>
        resolveSymbol3DAsset(symbol, {
          assetsEnabled: assetRenderingEnabled,
        }),
      [
        assetRenderingEnabled,
        symbol,
        symbol.libraryKey,
        symbol.threeD?.modelKey,
        symbol.threeD?.userModel?.modelId,
        symbol.threeD?.userModel?.status,
        symbol.threeD?.userModel?.url,
        symbol.threeD?.userModel?.sha256,
        symbol.threeD?.userModel?.version,
        symbol.threeD?.userModel?.pitchOffset,
        symbol.threeD?.userModel?.rollOffset,
        symbol.threeD?.userModel?.yawOffset,
        symbol.threeD?.modelPitchOffset,
        symbol.threeD?.modelRollOffset,
        symbol.threeD?.modelYawOffset,
        symbol.threeD?.userModel?.dimensions?.width,
        symbol.threeD?.userModel?.dimensions?.height,
        symbol.threeD?.userModel?.dimensions?.depth,
        symbol.threeD?.height,
        symbol.threeD?.dimensions?.width,
        symbol.threeD?.dimensions?.height,
        symbol.threeD?.dimensions?.depth,
        symbol.threeD?.rotation3D?.pitch,
        symbol.threeD?.rotation3D?.roll,
        symbol.threeD?.rotation3D?.yaw,
        symbol.threeD?.product?.renderMode,
        symbol.threeD?.product?.proxyModelKey,
        symbol.threeD?.product?.selectedImageUrl,
        symbol.threeD?.product?.asset?.id,
        symbol.threeD?.product?.asset?.status,
        symbol.threeD?.product?.asset?.glbUrl,
        symbol.threeD?.product?.asset?.quality?.sha256,
        symbol.threeD?.product?.dimensions?.width,
        symbol.threeD?.product?.dimensions?.height,
        symbol.threeD?.product?.dimensions?.depth,
        "svgLength" in symbol ? symbol.svgLength : undefined,
        "svgHeight" in symbol ? symbol.svgHeight : undefined,
        isSvgType(symbol)
          ? isSymbolAspectRatioLocked(symbol.lockAspectRatio)
          : undefined,
      ],
    );
    const primitiveRotationControlsVisible = Boolean(
      !editorStore.panning &&
        selectedForRotationControls &&
        primitiveRotationObject,
    );
    const setPrimitiveRotationGroupRef = React.useCallback(
      (group: THREE.Group | null) => {
        primitiveRotationGroupRef.current = group;
        setPrimitiveRotationObject(group);
      },
      [],
    );
    React.useLayoutEffect(() => {
      if (
        !primitiveRotationControlsVisible ||
        !primitiveTransformControlsRef.current
      ) {
        return;
      }
      return registerTransformControlsPointerGuard(
        primitiveTransformControlsRef.current,
      );
    }, [primitiveRotationControlsVisible]);
    const beginPrimitiveRotation = React.useCallback(() => {
      if (!primitiveRotationObject || primitiveRotateTransactionRef.current) {
        return;
      }
      primitiveRotateTransactionRef.current = true;
      primitiveRotateChangedRef.current = false;
      setIsPrimitiveRotationDragging(true);
      primitiveRotationStart3DRef.current = floorplannerStore.symbolsMap.get(
        symbol.id,
      )?.threeD?.rotation3D;
      primitiveRotationSensitivityRef.current =
        createRotationGizmoSensitivitySession(primitiveRotationObject);
      floorplannerStore.beginUndoTransaction("rotate 3D symbol");
      floorplannerStore.recordSymbolForUndo(symbol.id);
      onDragActiveChange?.(true);
    }, [
      floorplannerStore,
      onDragActiveChange,
      primitiveRotationObject,
      symbol.id,
    ]);
    const updatePrimitiveRotation = React.useCallback(() => {
      if (!primitiveRotationObject) return;
      applyRotationGizmoSensitivity(
        primitiveRotationObject,
        primitiveRotationSensitivityRef.current,
        {
          snapStep: editorStore.isShiftPressed
            ? ROTATION_GIZMO_SNAP_STEP
            : null,
        },
      );
      primitiveRotateChangedRef.current = true;
      invalidate();
    }, [editorStore.isShiftPressed, invalidate, primitiveRotationObject]);
    const finishPrimitiveRotation = React.useCallback(() => {
      if (!primitiveRotationObject || !primitiveRotateTransactionRef.current) {
        return;
      }
      primitiveRotateTransactionRef.current = false;
      setIsPrimitiveRotationDragging(false);
      primitiveRotationSensitivityRef.current = null;
      onDragActiveChange?.(false);

      if (!primitiveRotateChangedRef.current) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const currentSymbol = floorplannerStore.symbolsMap.get(symbol.id);
      if (!currentSymbol) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const nextRotation3D = getSymbol3DRotationFromObject(
        primitiveRotationObject,
      );
      const { rotation, rotation3D } = syncSymbolPlanRotationAfter3DYawChange(
        currentSymbol.rotation,
        primitiveRotationStart3DRef.current,
        nextRotation3D,
      );
      floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
        ...currentSymbol.threeD,
        rotation3D,
      });
      floorplannerStore.updateSymbolProperty(symbol.id, "rotation", rotation);
      floorplannerStore.recordSymbolChange(symbol.id, "modify");
      floorplannerStore.pushToUndoStack();
      invalidate();
    }, [
      floorplannerStore,
      invalidate,
      onDragActiveChange,
      primitiveRotationObject,
      symbol.id,
    ]);
    React.useEffect(
      () => () => {
        if (primitiveRotateTransactionRef.current) {
          primitiveRotateTransactionRef.current = false;
          setIsPrimitiveRotationDragging(false);
          floorplannerStore.cancelUndoTransaction();
          onDragActiveChange?.(false);
        }
      },
      [floorplannerStore, onDragActiveChange],
    );
    const primitiveRotationGizmoDimensions = useMemo(
      () =>
        geometry ? getLivingRoomGeometryGizmoDimensions(geometry.parts) : {},
      [geometry],
    );
    const updatePrimitiveRotationControlSize = React.useCallback(
      (currentCamera: THREE.Camera) => {
        if (
          !primitiveRotationControlsVisible ||
          !primitiveRotationObject ||
          !primitiveTransformControlsRef.current
        ) {
          return;
        }
        primitiveRotationObject.updateMatrixWorld(true);
        primitiveTransformControlsRef.current.setSize(
          getRotationGizmoControlSize(primitiveRotationGizmoDimensions, {
            camera: currentCamera,
            worldPosition: primitiveRotationObject.getWorldPosition(
              primitiveRotationWorldPositionRef.current,
            ),
          }),
        );
      },
      [
        primitiveRotationControlsVisible,
        primitiveRotationGizmoDimensions,
        primitiveRotationObject,
      ],
    );
    React.useLayoutEffect(() => {
      updatePrimitiveRotationControlSize(camera);
    }, [camera, updatePrimitiveRotationControlSize]);
    useFrame(({ camera }) => {
      updatePrimitiveRotationControlSize(camera);
    });

    if (!geometry) return null;

    const planScale = getSymbol3DPlanScale(symbol);
    const primitiveRotation = symbol.threeD?.rotation3D;
    const primitiveRotationControlSize = getRotationGizmoControlSize(
      primitiveRotationGizmoDimensions,
    );
    const primitiveFallback = (
      <>
        <group
          ref={setPrimitiveRotationGroupRef}
          {...(!isPrimitiveRotationDragging && {
            rotation: [
              primitiveRotation?.pitch ?? 0,
              primitiveRotation?.roll ?? 0,
              primitiveRotation?.yaw ?? 0,
            ],
          })}
        >
          {geometry.parts.map((part, index) => (
            <PrimitivePart
              key={`${part.id}-${index}`}
              symbol={symbol}
              part={part}
              selected={selected}
              {...pointerHandlers}
            />
          ))}
        </group>
        {primitiveRotationControlsVisible &&
          primitiveRotationObject &&
          createPortal(
            <TransformControls
              ref={primitiveTransformControlsRef}
              object={primitiveRotationObject}
              mode="rotate"
              space="local"
              rotationSnap={
                editorStore.isShiftPressed ? ROTATION_GIZMO_SNAP_STEP : null
              }
              size={primitiveRotationControlSize}
              onMouseDown={beginPrimitiveRotation}
              onObjectChange={updatePrimitiveRotation}
              onMouseUp={finishPrimitiveRotation}
            />,
            scene,
          )}
      </>
    );

    const groupTranslateOffset = getSymbol3DGroupTranslateOffset(symbol.id);
    const opacityRemountEpoch = getOpacityRemountEpoch(symbol.id);

    return (
      <group
        key={`opacity-remount-${opacityRemountEpoch}`}
        ref={groupRef}
        position={[
          symbol.position.x + groupTranslateOffset.x,
          symbol.position.y + groupTranslateOffset.y,
          0,
        ]}
        rotation={[0, 0, symbol.rotation ?? 0]}
        scale={planScale}
      >
        {assetResolution.mode === "asset3D" ? (
          <SymbolAssetErrorBoundary
            assetKey={`${assetResolution.asset.cacheKey}-${opacityRemountEpoch}`}
            fallback={primitiveFallback}
            onError={(error) =>
              trackSymbolAssetFallback(symbol.id, assetResolution.asset, error)
            }
          >
            <SymbolAsset3DModel
              symbol={symbol}
              asset={assetResolution.asset}
              selected={selected}
              rotationControlsVisible={selectedForRotationControls}
              fallback={primitiveFallback}
              onRotateActiveChange={onDragActiveChange}
              onLoadingChange={onAssetLoadingChange}
              {...pointerHandlers}
            />
          </SymbolAssetErrorBoundary>
        ) : assetResolution.mode === "billboardPreview" &&
          symbol.threeD?.product?.selectedImageUrl ? (
          <ProductPreviewBillboard
            symbol={symbol}
            selected={selected}
            {...pointerHandlers}
          />
        ) : (
          primitiveFallback
        )}
      </group>
    );
  },
);

function applyLooseSymbolVisualState(
  object: THREE.Object3D,
  state: Symbol3DDragVisualState,
) {
  object.position.set(state.position.x, state.position.y, 0);
  object.updateMatrixWorld();
}

const SHARED_BUSH_FOLIAGE_CYLINDER_GEOMETRY = new THREE.CylinderGeometry(
  1,
  1,
  1,
  6,
  1,
  false,
);

const InstancedBushFoliageCylinders: React.FC<{
  symbol: SymbolType;
  part: Extract<LivingRoom3DPart, { type: "instancedCylinders" }>;
  color: THREE.Color;
  commonProps: {
    position: [number, number, number];
    renderOrder?: number;
    castShadow: boolean;
    receiveShadow: boolean;
    onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
    onPointerMove: (event: ThreeEvent<PointerEvent>) => void;
    onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
    onPointerCancel: (event: ThreeEvent<PointerEvent>) => void;
    userData: Record<string, unknown>;
  };
  depthWrite?: boolean;
  offsetProps: {
    polygonOffset?: boolean;
    polygonOffsetFactor?: number;
  };
}> = ({ symbol, part, color, commonProps, depthWrite, offsetProps }) => {
  const meshRef = React.useRef<THREE.InstancedMesh>(null);
  const tempMatrix = useMemo(() => new THREE.Matrix4(), []);
  const tempPosition = useMemo(() => new THREE.Vector3(), []);
  const tempQuaternion = useMemo(() => new THREE.Quaternion(), []);
  const tempScale = useMemo(() => new THREE.Vector3(), []);
  const tempEuler = useMemo(() => new THREE.Euler(), []);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) {
      return;
    }

    part.instances.forEach((instance, index) => {
      tempPosition.set(
        instance.center[0],
        instance.center[1],
        instance.center[2],
      );
      tempEuler.set(Math.PI / 2, 0, instance.rotationZ ?? 0);
      tempQuaternion.setFromEuler(tempEuler);
      tempScale.set(instance.radiusX, instance.height, instance.radiusY);
      tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
      mesh.setMatrixAt(index, tempMatrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
  }, [
    part.instances,
    tempEuler,
    tempMatrix,
    tempPosition,
    tempQuaternion,
    tempScale,
  ]);

  return (
    <instancedMesh
      ref={meshRef}
      args={[
        SHARED_BUSH_FOLIAGE_CYLINDER_GEOMETRY,
        undefined,
        part.instances.length,
      ]}
      frustumCulled={false}
      {...commonProps}
    >
      <PrimitivePartMaterial
        symbol={symbol}
        materialSlot={part.materialSlot}
        color={color}
        roughness={0.78}
        side={THREE.DoubleSide}
        depthWrite={depthWrite}
        {...offsetProps}
      />
    </instancedMesh>
  );
};

const PrimitivePart: React.FC<{
  symbol: SymbolType;
  part: LivingRoom3DPart;
  selected: boolean;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
  onPointerCancel: (event: ThreeEvent<PointerEvent>) => void;
}> = ({
  symbol,
  part,
  selected,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}) => {
  const color = useMemo(() => {
    return resolvePrimitiveSymbolPartColor(
      symbol,
      part.materialSlot,
      part.useFillColor,
      part.type === "cylinder" ? part.ignoreSymbolFillColor : false,
    );
  }, [
    part,
    part.materialSlot,
    part.useFillColor,
    symbol,
    symbol.fillColor,
    symbol.threeD,
  ]);
  const extrudedShape = useMemo(() => {
    if (part.type !== "extrudedShape") return null;
    const [firstPoint, ...remainingPoints] = part.points;
    const shape = new THREE.Shape();
    shape.moveTo(firstPoint[0], firstPoint[1]);
    remainingPoints.forEach(([x, y]) => shape.lineTo(x, y));
    shape.closePath();
    part.holes?.forEach((holePoints) => {
      const [firstHolePoint, ...remainingHolePoints] = holePoints;
      if (!firstHolePoint) return;
      const hole = new THREE.Path();
      hole.moveTo(firstHolePoint[0], firstHolePoint[1]);
      remainingHolePoints.forEach(([x, y]) => hole.lineTo(x, y));
      hole.closePath();
      shape.holes.push(hole);
    });
    return shape;
  }, [part]);
  const extrudeSettings = useMemo(() => {
    if (part.type !== "extrudedShape") return null;
    const defaultBevel = Math.min(part.height * 0.03, 0.012);
    const bevelSize = part.bevelSize ?? defaultBevel;
    const bevelThickness = part.bevelThickness ?? defaultBevel;
    return {
      depth: part.height,
      bevelEnabled: bevelSize > 0 && bevelThickness > 0,
      bevelSize,
      bevelThickness,
      bevelSegments: part.bevelSegments ?? 2,
      curveSegments: part.curveSegments ?? 12,
    };
  }, [part]);
  const profiledExtrudedGeometry = useMemo(() => {
    if (part.type !== "extrudedShape" || !extrudeSettings) return null;

    if (part.backrestProfile) {
      const geometry = createLoungeChair2ExtrudedGeometry(
        part.points,
        extrudeSettings,
        part.holes,
      );
      applyLoungeChair1BackrestProfile(
        geometry,
        part.height,
        part.backrestProfile,
      );
      return geometry;
    }

    if (part.topSlopeMaxDrop == null) return null;

    const geometry = createLoungeChair2ExtrudedGeometry(
      part.points,
      extrudeSettings,
      part.holes,
    );
    applyLoungeChair2WrapperTopSlope(
      geometry,
      part.height,
      part.topSlopeMaxDrop,
    );
    return geometry;
  }, [extrudeSettings, part]);
  const pillowGeometry = useMemo(() => {
    if (part.type !== "pillow") return null;
    return createPillowBufferGeometry(
      part.size[0],
      part.size[1],
      part.size[2],
      part.segments,
      part.shape,
    );
  }, [part]);
  const softBoxGeometry = useMemo(() => {
    if (part.type !== "box" || !part.softness) return null;
    return createSoftBoxBufferGeometry(
      part.size[0],
      part.size[1],
      part.size[2],
      part.softness,
    );
  }, [part]);
  const slopedBasinGeometry = useMemo(() => {
    if (part.type !== "slopedBasin") return null;
    return createSlopedBasinBufferGeometry(
      part.topPoints,
      part.bottomPoints,
      part.topZ,
      part.bottomZ,
      part.verticalSegments,
    );
  }, [part]);
  const loungeChairShellGeometry = useMemo(() => {
    if (part.type !== "loungeChairShell") return null;
    if (part.variant === "loungeChair2") {
      return createLoungeChair2ShellBufferGeometry({
        width: part.width,
        depth: part.depth,
        height: part.height,
        armHeight: part.armHeight,
        pathSegments: part.backSegments,
        railSegments: part.railSegments,
      });
    }
    return createLoungeChair1ShellBufferGeometry({
      width: part.width,
      depth: part.depth,
      height: part.height,
      armHeight: part.armHeight,
      shellThickness: part.shellThickness,
      straightSegments: part.straightSegments,
      backSegments: part.backSegments,
      railSegments: part.railSegments,
    });
  }, [part]);
  const bonnieTorsoGeometry = useMemo(() => {
    if (part.type !== "bonnieTorso") return null;
    return createBonnieTorsoBufferGeometry(part.width, part.depth, part.height);
  }, [part]);
  const bonnieProfileGeometry = useMemo(() => {
    if (part.type !== "bonnieProfile") return null;
    return createBonnieProfileBufferGeometry(
      part.profile,
      part.radialSegments,
      part.verticalSegments,
      part.capStart,
      part.capEnd,
    );
  }, [part]);
  useEffect(() => {
    return () => {
      bonnieProfileGeometry?.dispose();
      bonnieTorsoGeometry?.dispose();
      loungeChairShellGeometry?.dispose();
      pillowGeometry?.dispose();
      softBoxGeometry?.dispose();
      slopedBasinGeometry?.dispose();
      profiledExtrudedGeometry?.dispose();
    };
  }, [
    bonnieProfileGeometry,
    bonnieTorsoGeometry,
    loungeChairShellGeometry,
    pillowGeometry,
    profiledExtrudedGeometry,
    slopedBasinGeometry,
    softBoxGeometry,
  ]);
  const position: [number, number, number] =
    part.type === "extrudedShape"
      ? [part.center[0], part.center[1], part.z]
      : part.type === "instancedCylinders"
        ? [0, 0, 0]
        : part.center;
  const commonProps = {
    position,
    renderOrder: part.renderOrder,
    castShadow: "castShadow" in part ? (part.castShadow ?? true) : true,
    receiveShadow:
      "receiveShadow" in part ? (part.receiveShadow ?? true) : true,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    userData: {
      floorplannerType: "symbol",
      floorplannerId: symbol.id,
      floorplannerSymbolKind: symbol.type,
      floorplannerPrimitivePart: part.id,
    },
  };

  const offsetProps = getPartPolygonOffsetProps(part);
  const depthWrite = getPartDepthWrite(part);
  const showSelectionOutline =
    selected && !("skipSelectionOutline" in part && part.skipSelectionOutline);

  if (part.type === "instancedCylinders") {
    return (
      <InstancedBushFoliageCylinders
        symbol={symbol}
        part={part}
        color={color}
        commonProps={commonProps}
        depthWrite={depthWrite}
        offsetProps={offsetProps}
      />
    );
  }

  if (part.type === "box") {
    return (
      <mesh
        {...commonProps}
        rotation={[
          part.rotationX ?? 0,
          part.rotationY ?? 0,
          part.rotationZ ?? 0,
        ]}
      >
        {softBoxGeometry ? (
          <primitive object={softBoxGeometry} attach="geometry" />
        ) : (
          <boxGeometry args={part.size} />
        )}
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={part.softness ? 0.9 : 0.76}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (part.type === "cylinder") {
    return (
      <mesh
        {...commonProps}
        rotation={[
          Math.PI / 2 + (part.rotationX ?? 0),
          part.rotationY ?? 0,
          part.rotationZ ?? 0,
        ]}
        scale={[part.radiusX, 1, part.radiusY]}
      >
        <cylinderGeometry
          args={[
            part.radiusTopScale ?? 1,
            part.radiusBottomScale ?? 1,
            part.height,
            part.radialSegments,
            1,
            false,
          ]}
        />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={0.78}
          side={THREE.DoubleSide}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (part.type === "ellipsoid") {
    return (
      <mesh
        {...commonProps}
        rotation={[
          part.rotationX ?? 0,
          part.rotationY ?? 0,
          part.rotationZ ?? 0,
        ]}
        scale={[part.radiusX, part.radiusY, part.radiusZ]}
      >
        <sphereGeometry args={[1, part.widthSegments, part.heightSegments]} />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={0.68}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (part.type === "bonnieTorso") {
    if (!bonnieTorsoGeometry) return null;
    return (
      <mesh {...commonProps}>
        <primitive object={bonnieTorsoGeometry} attach="geometry" />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={0.88}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (part.type === "bonnieProfile") {
    if (!bonnieProfileGeometry) return null;
    return (
      <mesh {...commonProps}>
        <primitive object={bonnieProfileGeometry} attach="geometry" />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={0.88}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (part.type === "pillow" && pillowGeometry) {
    return (
      <mesh
        {...commonProps}
        rotation={[
          part.rotationX ?? 0,
          part.rotationY ?? 0,
          part.rotationZ ?? 0,
        ]}
      >
        <primitive object={pillowGeometry} attach="geometry" />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={part.roughness ?? 0.95}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (part.type === "loungeChairShell") {
    if (!loungeChairShellGeometry) return null;
    return (
      <mesh {...commonProps}>
        <primitive object={loungeChairShellGeometry} attach="geometry" />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={0.92}
          side={THREE.DoubleSide}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (part.type === "slopedBasin") {
    if (!slopedBasinGeometry) return null;
    return (
      <mesh {...commonProps}>
        <primitive object={slopedBasinGeometry} attach="geometry" />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={0.76}
          side={THREE.DoubleSide}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  if (!extrudedShape || !extrudeSettings) return null;

  if (profiledExtrudedGeometry) {
    return (
      <mesh
        {...commonProps}
        rotation={[
          part.rotationX ?? 0,
          part.rotationY ?? 0,
          part.rotationZ ?? 0,
        ]}
      >
        <primitive object={profiledExtrudedGeometry} attach="geometry" />
        <PrimitivePartMaterial
          symbol={symbol}
          materialSlot={part.materialSlot}
          color={color}
          roughness={0.78}
          side={THREE.DoubleSide}
          depthWrite={depthWrite}
          {...offsetProps}
        />
        <SelectedMeshOutline visible={showSelectionOutline} />
      </mesh>
    );
  }

  return (
    <mesh
      {...commonProps}
      rotation={[part.rotationX ?? 0, part.rotationY ?? 0, part.rotationZ ?? 0]}
    >
      <extrudeGeometry args={[extrudedShape, extrudeSettings]} />
      <PrimitivePartMaterial
        symbol={symbol}
        materialSlot={part.materialSlot}
        color={color}
        roughness={0.78}
        side={THREE.DoubleSide}
        depthWrite={depthWrite}
        {...offsetProps}
      />
      <SelectedMeshOutline visible={selected} />
    </mesh>
  );
};

const PrimitivePartMaterial: React.FC<{
  symbol: SymbolType;
  materialSlot: FloorPlan3DMaterialSlot;
  color: THREE.ColorRepresentation;
  roughness: number;
  side?: THREE.Side;
  polygonOffset?: boolean;
  polygonOffsetFactor?: number;
  depthWrite?: boolean;
}> = ({
  symbol,
  materialSlot,
  color,
  roughness,
  side,
  polygonOffset,
  polygonOffsetFactor,
  depthWrite,
}) => {
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  useBuiltInMaterialSurfaceMappingsRevision();
  const factor = polygonOffsetFactor ?? (polygonOffset ? -2 : undefined);
  const slotAssigned = hasAssignedSlotMaterial(symbol, materialSlot);
  const surfaceRef = resolvePrimitiveSymbolPartSurfaceRef(symbol, materialSlot);
  const partMaterial = resolvePrimitiveSymbolPartMaterial(symbol, materialSlot);
  const fillPattern = slotAssigned ? undefined : resolveSymbolPattern(symbol);
  const fillOpacity = resolveFillOpacity(symbol);
  const fillIsTransparent = fillOpacity < 1;
  const resolvedDepthWrite = fillIsTransparent ? false : depthWrite;
  const slotDefaults = floorplannerStore.threeDMaterialDefaults[materialSlot];
  const presetDefaults = partMaterial.builtIn
    ? {
        roughness: partMaterial.builtIn.roughness,
        metalness: partMaterial.builtIn.metalness,
      }
    : undefined;
  const resolvedRoughness =
    surfaceRef?.roughness ??
    partMaterial.roughness ??
    slotDefaults?.roughness ??
    roughness;
  const resolvedMetalness =
    surfaceRef?.metalness ??
    partMaterial.metalness ??
    slotDefaults?.metalness ??
    0;

  if (fillPattern) {
    return (
      <LitPatternMaterial
        fillColor={resolvePrimitiveSymbolFillColor(symbol)}
        fillPattern={fillPattern}
        coordinateSpace="world-triplanar"
        roughness={resolvedRoughness}
        metalness={resolvedMetalness}
        side={side}
        opacity={fillOpacity}
        transparent={fillIsTransparent}
        depthWrite={resolvedDepthWrite}
        polygonOffset={polygonOffset}
        polygonOffsetFactor={factor}
        polygonOffsetUnits={factor}
      />
    );
  }

  if (slotAssigned && partMaterial.isGlass && partMaterial.builtIn) {
    return (
      <meshPhysicalMaterial
        color={color}
        roughness={resolvedRoughness}
        metalness={0}
        transmission={partMaterial.builtIn.transmission ?? 1}
        thickness={partMaterial.builtIn.thickness ?? 0.05}
        ior={partMaterial.builtIn.ior ?? 1.5}
        transparent
        opacity={fillOpacity < 1 ? fillOpacity : 1}
        side={side}
        depthWrite={resolvedDepthWrite}
        polygonOffset={polygonOffset}
        polygonOffsetFactor={factor}
        polygonOffsetUnits={factor}
      />
    );
  }

  if (slotAssigned) {
    return (
      <CatalogStandardMaterial
        color={color}
        surfaceRef={surfaceRef}
        defaults={presetDefaults ?? slotDefaults}
        side={side}
        opacity={fillOpacity}
        transparent={fillIsTransparent}
        depthWrite={resolvedDepthWrite}
      />
    );
  }

  return (
    <meshStandardMaterial
      color={color}
      roughness={resolvedRoughness}
      metalness={resolvedMetalness}
      side={side}
      opacity={fillOpacity}
      transparent={fillIsTransparent}
      depthWrite={resolvedDepthWrite}
      polygonOffset={polygonOffset}
      polygonOffsetFactor={factor}
      polygonOffsetUnits={factor}
    />
  );
};

function getPartPolygonOffsetProps(part: LivingRoom3DPart) {
  if (!part.polygonOffset) {
    return { polygonOffset: undefined, polygonOffsetFactor: undefined };
  }
  return {
    polygonOffset: true as const,
    polygonOffsetFactor: part.polygonOffsetFactor,
  };
}

function getPartDepthWrite(part: LivingRoom3DPart): boolean | undefined {
  return part.depthWrite;
}

const ProductPreviewBillboard: React.FC<{
  symbol: SymbolType;
  selected: boolean;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
  onPointerCancel: (event: ThreeEvent<PointerEvent>) => void;
}> = ({
  symbol,
  selected,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}) => {
  const { gl, invalidate, scene } = useThree();
  const imageUrl = symbol.threeD?.product?.selectedImageUrl;
  const [texture, setTexture] = React.useState<THREE.Texture | null>(null);
  const materialRef = React.useRef<THREE.MeshBasicMaterial | null>(null);
  const width = isSvgType(symbol)
    ? symbol.svgLength
    : (symbol.threeD?.dimensions?.width ?? 1);
  const depth = isSvgType(symbol)
    ? symbol.svgHeight
    : (symbol.threeD?.dimensions?.depth ?? 0.8);
  const height =
    symbol.threeD?.dimensions?.height ?? symbol.threeD?.height ?? 0.8;
  const previewWidth = Math.max(width, depth, 0.35);
  const previewHeight = Math.max(height, 0.35);
  const fillTint = shouldUseSymbolFillColorIn3D(symbol)
    ? symbol.fillColor
    : 0xffffff;

  React.useEffect(() => {
    let cancelled = false;
    if (!imageUrl) {
      setTexture(null);
      return;
    }

    loadCatalogTexture(imageUrl, gl)
      .then((loadedTexture) => {
        if (cancelled) return;
        loadedTexture.wrapS = THREE.ClampToEdgeWrapping;
        loadedTexture.wrapT = THREE.ClampToEdgeWrapping;
        loadedTexture.repeat.set(1, 1);
        loadedTexture.needsUpdate = true;
        setTexture(loadedTexture);
        invalidate();
      })
      .catch(() => {
        if (cancelled) return;
        setTexture(null);
        invalidate();
      });

    return () => {
      cancelled = true;
    };
  }, [gl, imageUrl, invalidate]);

  React.useEffect(() => {
    const material = materialRef.current;
    if (!material) return;
    material.needsUpdate = true;
    invalidate();
  }, [invalidate, texture, fillTint]);

  return (
    <mesh
      position={[0, 0, previewHeight / 2]}
      rotation={[Math.PI / 2, 0, 0]}
      castShadow={false}
      receiveShadow={false}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      userData={{
        floorplannerType: "symbol",
        floorplannerId: symbol.id,
        floorplannerSymbolKind: symbol.type,
        floorplannerPrimitivePart: "product-preview",
      }}
    >
      <planeGeometry args={[previewWidth, previewHeight]} />
      <meshBasicMaterial
        ref={materialRef}
        key={imageUrl ?? "solid"}
        color={fillTint}
        map={texture ?? undefined}
        side={THREE.DoubleSide}
        transparent
      />
      <SelectedMeshOutline visible={selected} />
    </mesh>
  );
};

function getLivingRoomGeometryGizmoDimensions(parts: LivingRoom3DPart[]): {
  width?: number;
  depth?: number;
  height?: number;
} {
  const bounds = new THREE.Box3();
  parts.forEach((part) => {
    if (part.type === "box") {
      const [width, depth, height] = part.size;
      expandBoundsByCenterAndSize(
        bounds,
        part.center,
        new THREE.Vector3(width, depth, height),
      );
      return;
    }

    if (part.type === "cylinder") {
      expandBoundsByCenterAndSize(
        bounds,
        part.center,
        new THREE.Vector3(part.radiusX * 2, part.radiusY * 2, part.height),
      );
      return;
    }

    if (part.type === "ellipsoid") {
      expandBoundsByCenterAndSize(
        bounds,
        part.center,
        new THREE.Vector3(part.radiusX * 2, part.radiusY * 2, part.radiusZ * 2),
      );
      return;
    }

    if (part.type === "bonnieTorso") {
      expandBoundsByCenterAndSize(
        bounds,
        [part.center[0], part.center[1], part.center[2] + part.height / 2],
        new THREE.Vector3(part.width, part.depth, part.height),
      );
      return;
    }

    if (part.type === "bonnieProfile") {
      part.profile.forEach((point) => {
        bounds.expandByPoint(
          new THREE.Vector3(
            part.center[0] + point.center[0] - point.radiusX,
            part.center[1] + point.center[1] - point.radiusY,
            part.center[2] + point.center[2],
          ),
        );
        bounds.expandByPoint(
          new THREE.Vector3(
            part.center[0] + point.center[0] + point.radiusX,
            part.center[1] + point.center[1] + point.radiusY,
            part.center[2] + point.center[2],
          ),
        );
      });
      return;
    }

    if (part.type === "pillow") {
      expandBoundsByCenterAndSize(
        bounds,
        part.center,
        new THREE.Vector3(part.size[0], part.size[1], part.size[2]),
      );
      return;
    }

    if (part.type === "loungeChairShell") {
      expandBoundsByCenterAndSize(
        bounds,
        [part.center[0], part.center[1], part.center[2] + part.height / 2],
        new THREE.Vector3(part.width, part.depth, part.height),
      );
      return;
    }

    if (part.type === "slopedBasin") {
      const points = [...part.topPoints, ...part.bottomPoints];
      if (points.length === 0) return;
      const minX = Math.min(...points.map(([x]) => x));
      const maxX = Math.max(...points.map(([x]) => x));
      const minY = Math.min(...points.map(([, y]) => y));
      const maxY = Math.max(...points.map(([, y]) => y));
      bounds.expandByPoint(
        new THREE.Vector3(
          part.center[0] + minX,
          part.center[1] + minY,
          part.center[2] + part.bottomZ,
        ),
      );
      bounds.expandByPoint(
        new THREE.Vector3(
          part.center[0] + maxX,
          part.center[1] + maxY,
          part.center[2] + part.topZ,
        ),
      );
      return;
    }

    if (part.type === "instancedCylinders") {
      part.instances.forEach((instance) => {
        expandBoundsByCenterAndSize(
          bounds,
          instance.center,
          new THREE.Vector3(
            instance.radiusX * 2,
            instance.radiusY * 2,
            instance.height,
          ),
        );
      });
      return;
    }

    if (part.type !== "extrudedShape" || part.points.length === 0) {
      return;
    }

    const minX = Math.min(...part.points.map(([x]) => x));
    const maxX = Math.max(...part.points.map(([x]) => x));
    const minY = Math.min(...part.points.map(([, y]) => y));
    const maxY = Math.max(...part.points.map(([, y]) => y));
    bounds.expandByPoint(new THREE.Vector3(minX, minY, part.z));
    bounds.expandByPoint(new THREE.Vector3(maxX, maxY, part.z + part.height));
  });

  if (bounds.isEmpty()) {
    return {};
  }

  const size = bounds.getSize(new THREE.Vector3());
  return {
    width: size.x,
    depth: size.y,
    height: size.z,
  };
}

function expandBoundsByCenterAndSize(
  bounds: THREE.Box3,
  center: [number, number, number],
  size: THREE.Vector3,
) {
  const centerVector = new THREE.Vector3(...center);
  const halfSize = size.multiplyScalar(0.5);
  bounds.expandByPoint(centerVector.clone().sub(halfSize));
  bounds.expandByPoint(centerVector.clone().add(halfSize));
}

class SymbolAssetErrorBoundary extends React.Component<
  {
    assetKey: string;
    children: React.ReactNode;
    fallback: React.ReactNode;
    onError: (error: unknown) => void;
  },
  { failed: boolean; failedAssetKey: string | null }
> {
  state = { failed: false, failedAssetKey: null };

  static getDerivedStateFromError(_error: unknown): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    this.setState({ failed: true, failedAssetKey: this.props.assetKey });
    this.props.onError(error);
  }

  componentDidUpdate(prevProps: { assetKey: string }) {
    if (prevProps.assetKey !== this.props.assetKey && this.state.failed) {
      this.setState({ failed: false, failedAssetKey: null });
    }
  }

  render() {
    if (
      this.state.failed &&
      (!this.state.failedAssetKey ||
        this.state.failedAssetKey === this.props.assetKey)
    ) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const SymbolAsset3DModel: React.FC<{
  symbol: SymbolType;
  asset: ResolvedSymbol3DAsset;
  selected: boolean;
  rotationControlsVisible: boolean;
  fallback: React.ReactNode;
  onRotateActiveChange?: (active: boolean) => void;
  onLoadingChange?: (symbolId: string, loading: boolean) => void;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
  onPointerCancel: (event: ThreeEvent<PointerEvent>) => void;
}> = observer(
  ({
    symbol,
    asset,
    selected,
    rotationControlsVisible,
    fallback,
    onRotateActiveChange,
    onLoadingChange,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const editorStore = React.useContext(editorStoreContext);
    const { camera, gl, invalidate, scene } = useThree();
    const modelWorldPositionRef = React.useRef(new THREE.Vector3());
    const transformControlsRef = React.useRef<TransformControlsImpl | null>(
      null,
    );
    const rotateTransactionRef = React.useRef(false);
    const rotateChangedRef = React.useRef(false);
    const rotationStart3DRef = React.useRef<Symbol3DRotation | undefined>(
      undefined,
    );
    const rotationSensitivityRef =
      React.useRef<RotationGizmoSensitivitySession | null>(null);
    const [isRotationGizmoDragging, setIsRotationGizmoDragging] =
      React.useState(false);
    const [rotationObject, setRotationObject] =
      React.useState<THREE.Group | null>(null);
    const setRotationGroupRef = React.useCallback(
      (group: THREE.Group | null) => {
        setRotationObject(group);
      },
      [],
    );
    const gltfState = useOptionalSymbolAssetGLTF(asset, gl, symbol.id);
    const gltf = gltfState.gltf;
    const assetLoading = !gltfState.failed && !gltf;
    React.useEffect(() => {
      onLoadingChange?.(symbol.id, assetLoading);
    }, [asset.cacheKey, assetLoading, onLoadingChange, symbol.id]);
    React.useEffect(
      () => () => {
        onLoadingChange?.(symbol.id, false);
      },
      [onLoadingChange, symbol.id],
    );
    const assetMeshState = React.useMemo(() => {
      if (!gltf) return null;
      return createSymbolAssetMeshInstance(
        gltf.scene,
        asset.source,
        asset.version,
        asset.yawOffset,
        asset.pitchOffset,
        asset.rollOffset,
      );
    }, [
      asset.pitchOffset,
      asset.rollOffset,
      asset.source,
      asset.version,
      asset.yawOffset,
      gltf?.scene,
    ]);
    const assetRoot = assetMeshState?.assetRoot;
    const meshBounds = assetMeshState?.meshBounds;
    const rotation3D = symbol.threeD?.rotation3D;
    const orientedMeshBounds = React.useMemo(
      () =>
        meshBounds
          ? getSymbolAssetOrientedBounds(meshBounds, rotation3D)
          : null,
      [meshBounds, rotation3D?.pitch, rotation3D?.roll, rotation3D?.yaw],
    );
    const lockAspectRatio = isSvgType(symbol)
      ? isSymbolAspectRatioLocked(symbol.lockAspectRatio)
      : false;
    const transform = React.useMemo(
      () =>
        meshBounds
          ? computeFloorplannerAssetTransform({
              modelBounds: meshBounds,
              targetDimensions: asset.dimensions,
              baseElevation: symbol.threeD?.baseElevation,
              fitMode: getSymbol3DAssetFitMode(
                asset.source,
                Boolean(lockAspectRatio),
              ),
            })
          : null,
      [
        asset.dimensions.depth,
        asset.dimensions.height,
        asset.dimensions.width,
        asset.source,
        lockAspectRatio,
        meshBounds,
        symbol.threeD?.baseElevation,
      ],
    );
    const showRotationControls = Boolean(
      !editorStore.panning &&
        selected &&
        rotationControlsVisible &&
        rotationObject &&
        assetRoot,
    );
    React.useLayoutEffect(() => {
      if (!showRotationControls || !transformControlsRef.current) return;
      return registerTransformControlsPointerGuard(
        transformControlsRef.current,
      );
    }, [showRotationControls]);
    React.useLayoutEffect(() => {
      const controls = transformControlsRef.current;
      if (!controls) return;
      const controlsWithSnap = controls as unknown as {
        rotationSnap: number | null;
      };
      controlsWithSnap.rotationSnap = editorStore.isShiftPressed
        ? ROTATION_GIZMO_SNAP_STEP
        : null;
    }, [editorStore.isShiftPressed, rotationObject]);
    const beginModelRotation = React.useCallback(() => {
      if (!rotationObject || rotateTransactionRef.current) return;
      rotateTransactionRef.current = true;
      rotateChangedRef.current = false;
      setIsRotationGizmoDragging(true);
      rotationStart3DRef.current = floorplannerStore.symbolsMap.get(
        symbol.id,
      )?.threeD?.rotation3D;
      rotationSensitivityRef.current =
        createRotationGizmoSensitivitySession(rotationObject);
      floorplannerStore.beginUndoTransaction("rotate 3D object");
      floorplannerStore.recordSymbolForUndo(symbol.id);
      onRotateActiveChange?.(true);
    }, [floorplannerStore, onRotateActiveChange, rotationObject, symbol.id]);
    const updateModelRotation = React.useCallback(() => {
      if (!rotationObject) return;
      applyRotationGizmoSensitivity(
        rotationObject,
        rotationSensitivityRef.current,
        {
          snapStep: editorStore.isShiftPressed
            ? ROTATION_GIZMO_SNAP_STEP
            : null,
        },
      );
      rotateChangedRef.current = true;
      invalidate();
    }, [editorStore.isShiftPressed, invalidate, rotationObject]);
    const handlePointerDown = React.useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        if (
          isPointerOverTransformControlsPicker(
            transformControlsRef.current,
            event,
          )
        ) {
          return;
        }
        onPointerDown(event);
      },
      [onPointerDown],
    );
    const finishModelRotation = React.useCallback(() => {
      if (!rotationObject || !rotateTransactionRef.current) return;
      rotateTransactionRef.current = false;
      setIsRotationGizmoDragging(false);
      rotationSensitivityRef.current = null;
      onRotateActiveChange?.(false);

      if (!rotateChangedRef.current) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const currentSymbol = floorplannerStore.symbolsMap.get(symbol.id);
      if (!currentSymbol) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const nextRotation3D = getSymbol3DRotationFromObject(rotationObject);
      const { rotation, rotation3D } = syncSymbolPlanRotationAfter3DYawChange(
        currentSymbol.rotation,
        rotationStart3DRef.current,
        nextRotation3D,
      );
      const userModel = currentSymbol.threeD?.userModel;
      const stableUserModelDimensions = userModel
        ? {
            width:
              positiveDimension(asset.dimensions.width) ??
              userModel.dimensions?.width ??
              currentSymbol.threeD?.dimensions?.width,
            depth:
              positiveDimension(asset.dimensions.depth) ??
              userModel.dimensions?.depth ??
              currentSymbol.threeD?.dimensions?.depth,
            height:
              positiveDimension(asset.dimensions.height) ??
              userModel.dimensions?.height ??
              currentSymbol.threeD?.dimensions?.height,
          }
        : undefined;
      floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
        ...currentSymbol.threeD,
        rotation3D,
        ...(userModel && stableUserModelDimensions
          ? {
              dimensions: {
                ...currentSymbol.threeD?.dimensions,
                ...stableUserModelDimensions,
              },
              userModel: {
                ...userModel,
                dimensions: {
                  ...userModel.dimensions,
                  ...stableUserModelDimensions,
                },
              },
            }
          : {}),
      });
      floorplannerStore.updateSymbolProperty(symbol.id, "rotation", rotation);
      // An uploaded model carries a 2D silhouette baked at its original
      // orientation. Once it has been tipped upright in 3D that silhouette is a
      // side view, so the plan outline is rebuilt from the rotated footprint -
      // and restored from the upload once the model lies flat again.
      if (meshBounds && transform && isUploadedModel3DSymbol(currentSymbol)) {
        if (tiltsModelOutOfPlan(rotation3D)) {
          const footprint =
            (assetRoot &&
              createSymbolAssetFootprintSvg(
                assetRoot,
                rotation3D,
                transform.scale,
              )) ??
            getSymbolPlanFootprint(
              getSymbolAssetOrientedBounds(meshBounds, rotation3D),
              transform.scale,
            );
          if (footprint) {
            floorplannerStore.updateSymbolProperty(
              symbol.id,
              "svgPath",
              "svgPath" in footprint
                ? footprint.svgPath
                : createSymbolFootprintOutlineSvg(footprint),
            );
            floorplannerStore.updateSymbolProperty(
              symbol.id,
              "svgLength",
              footprint.width,
              { preserveUserModel3DTargetDimensions: true },
            );
            floorplannerStore.updateSymbolProperty(
              symbol.id,
              "svgHeight",
              footprint.depth,
              { preserveUserModel3DTargetDimensions: true },
            );
          }
        } else if (currentSymbol.threeD?.userModel?.footprintSvgPath) {
          const footprint = getSymbolPlanFootprint(
            getSymbolAssetOrientedBounds(meshBounds, rotation3D),
            transform.scale,
          );
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "svgPath",
            currentSymbol.threeD.userModel.footprintSvgPath,
          );
          if (footprint) {
            floorplannerStore.updateSymbolProperty(
              symbol.id,
              "svgLength",
              footprint.width,
              { preserveUserModel3DTargetDimensions: true },
            );
            floorplannerStore.updateSymbolProperty(
              symbol.id,
              "svgHeight",
              footprint.depth,
              { preserveUserModel3DTargetDimensions: true },
            );
          }
        }
      }
      floorplannerStore.recordSymbolChange(symbol.id, "modify");
      floorplannerStore.pushToUndoStack();
      invalidate();
    }, [
      asset.dimensions.depth,
      asset.dimensions.height,
      asset.dimensions.width,
      floorplannerStore,
      invalidate,
      assetRoot,
      meshBounds,
      onRotateActiveChange,
      rotationObject,
      symbol.id,
      transform,
    ]);
    React.useEffect(
      () => () => {
        if (rotateTransactionRef.current) {
          rotateTransactionRef.current = false;
          setIsRotationGizmoDragging(false);
          floorplannerStore.cancelUndoTransaction();
          onRotateActiveChange?.(false);
        }
      },
      [floorplannerStore, onRotateActiveChange],
    );
    const interactionProxy = React.useMemo(() => {
      if (!orientedMeshBounds || !transform) return null;
      const size = orientedMeshBounds.getSize(new THREE.Vector3());
      const center = orientedMeshBounds.getCenter(new THREE.Vector3());
      const [scaleX, scaleY, scaleZ] = getSymbol3DAssetScaleComponents(
        transform.scale,
      );
      const safeScaleX = positiveDimension(scaleX) ?? 1;
      const safeScaleY = positiveDimension(scaleY) ?? 1;
      const footprintWidth = positiveDimension(asset.dimensions.width);
      const footprintDepth = positiveDimension(asset.dimensions.depth);
      const footprintSize =
        asset.source === "userModel"
          ? new THREE.Vector3(
              Math.max(
                (footprintWidth ?? size.x * safeScaleX) / safeScaleX,
                0.05,
              ),
              Math.max(
                (footprintDepth ?? size.y * safeScaleY) / safeScaleY,
                0.05,
              ),
              Math.max(size.z, 0.05),
            )
          : undefined;
      return {
        center,
        size:
          footprintSize ??
          new THREE.Vector3(
            Math.max(size.x, 0.05),
            Math.max(size.y, 0.05),
            Math.max(size.z, 0.05),
          ),
      };
    }, [
      asset.dimensions.depth,
      asset.dimensions.width,
      asset.source,
      orientedMeshBounds,
      transform,
    ]);
    const rotationControlDimensions = React.useMemo(() => {
      if (!transform || !interactionProxy) return {};
      const [scaleX, scaleY, scaleZ] = getSymbol3DAssetScaleComponents(
        transform.scale,
      );
      return {
        width: interactionProxy.size.x * scaleX,
        depth: interactionProxy.size.y * scaleY,
        height: interactionProxy.size.z * scaleZ,
      };
    }, [interactionProxy, transform]);
    const updateModelRotationControlSize = React.useCallback(
      (currentCamera: THREE.Camera) => {
        if (
          !showRotationControls ||
          !rotationObject ||
          !transformControlsRef.current
        ) {
          return;
        }
        rotationObject.updateMatrixWorld(true);
        transformControlsRef.current.setSize(
          getRotationGizmoControlSize(rotationControlDimensions, {
            camera: currentCamera,
            worldPosition: rotationObject.getWorldPosition(
              modelWorldPositionRef.current,
            ),
          }),
        );
      },
      [rotationControlDimensions, rotationObject, showRotationControls],
    );
    React.useLayoutEffect(() => {
      updateModelRotationControlSize(camera);
    }, [camera, updateModelRotationControlSize]);
    useFrame(({ camera }) => {
      updateModelRotationControlSize(camera);
    });

    React.useEffect(() => {
      if (!assetRoot) return;
      assetRoot.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.raycast = noopRaycast;
          child.castShadow = asset.source !== "userModel";
          child.receiveShadow = true;
          prepareSymbolAssetMeshMaterial(child, {
            fillColor: shouldUseSymbolFillColorIn3D(symbol)
              ? symbol.fillColor
              : undefined,
            opacity: resolveFillOpacity(symbol),
            applyUnreadableUserModelFallback: asset.source === "userModel",
          });
        }
      });
      trackFloorplan3DMetric("symbol_3d_asset_load", {
        symbolId: symbol.id,
        source: asset.source,
        assetId: asset.id,
        url: asset.url,
      });
      // Re-run the scene fade layout pass so it multiplies the new absolute
      // fill opacity by the current transition opacity (if any).
      invalidate();
    }, [
      asset.id,
      asset.source,
      asset.url,
      assetRoot,
      invalidate,
      symbol.fillColor,
      symbol.fillOpacity,
      symbol.id,
      symbol.threeD?.fillColorOverride,
    ]);

    if (gltfState.failed) {
      return <>{fallback}</>;
    }
    if (!assetRoot || !transform || !interactionProxy) return null;
    const scaleComponents = getSymbol3DAssetScaleComponents(transform.scale);
    const rotationControlSize = getRotationGizmoControlSize(
      rotationControlDimensions,
    );

    return (
      <group
        scale={scaleComponents}
        position={transform.position}
        userData={{
          floorplannerType: "symbol",
          floorplannerId: symbol.id,
          floorplannerSymbolKind: symbol.type,
          floorplannerPrimitivePart: `${asset.source}-3d-asset`,
        }}
      >
        <mesh
          position={interactionProxy.center}
          raycast={meshRaycast}
          onPointerDown={handlePointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          userData={{
            floorplannerType: "symbol",
            floorplannerId: symbol.id,
            floorplannerSymbolKind: symbol.type,
            floorplannerPrimitivePart: `${asset.source}-3d-asset-hitbox`,
          }}
        >
          <boxGeometry
            args={[
              interactionProxy.size.x,
              interactionProxy.size.y,
              interactionProxy.size.z,
            ]}
          />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          <SelectedMeshOutline visible={selected} />
        </mesh>
        <group
          ref={setRotationGroupRef}
          {...(!isRotationGizmoDragging && {
            rotation: [
              rotation3D?.pitch ?? 0,
              rotation3D?.roll ?? 0,
              rotation3D?.yaw ?? 0,
            ],
          })}
        >
          <primitive object={assetRoot} />
        </group>
        {showRotationControls &&
          rotationObject &&
          createPortal(
            <TransformControls
              ref={transformControlsRef}
              object={rotationObject}
              mode="rotate"
              space="local"
              rotationSnap={
                editorStore.isShiftPressed ? ROTATION_GIZMO_SNAP_STEP : null
              }
              size={rotationControlSize}
              onMouseDown={beginModelRotation}
              onObjectChange={updateModelRotation}
              onMouseUp={finishModelRotation}
            />,
            scene,
          )}
      </group>
    );
  },
);

function useOptionalSymbolAssetGLTF(
  asset: ResolvedSymbol3DAsset,
  gl: THREE.WebGLRenderer,
  symbolId: string,
): { gltf: LoadedSymbolGLTF | null; failed: boolean } {
  const [state, setState] = React.useState<{
    cacheKey: string;
    gltf: LoadedSymbolGLTF | null;
    failed: boolean;
  }>({
    cacheKey: asset.cacheKey,
    gltf: null,
    failed: false,
  });

  React.useEffect(() => {
    let cancelled = false;

    if (isSymbol3DAssetUnavailable(asset)) {
      setState((previous) =>
        previous.cacheKey === asset.cacheKey && previous.failed
          ? previous
          : { cacheKey: asset.cacheKey, gltf: null, failed: true },
      );
      return;
    }

    const cached = symbolAssetLoadCache.get(asset.cacheKey);
    if (cached?.status === "loaded") {
      setState((previous) =>
        previous.cacheKey === asset.cacheKey &&
        previous.gltf === cached.gltf &&
        !previous.failed
          ? previous
          : { cacheKey: asset.cacheKey, gltf: cached.gltf, failed: false },
      );
      return;
    }

    setState((previous) =>
      previous.cacheKey === asset.cacheKey && previous.gltf
        ? previous
        : { cacheKey: asset.cacheKey, gltf: null, failed: false },
    );

    loadSymbolAssetGLTF(asset, gl)
      .then((loadedGltf) => {
        if (cancelled) return;
        setState({
          cacheKey: asset.cacheKey,
          gltf: loadedGltf,
          failed: false,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        if (isMissingSymbol3DAssetLoadError(error)) {
          markSymbol3DAssetUnavailable(asset);
        }
        trackSymbolAssetFallback(symbolId, asset, error);
        setState({ cacheKey: asset.cacheKey, gltf: null, failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [
    asset.cacheKey,
    asset.compression?.textures,
    asset.id,
    asset.source,
    asset.url,
    gl,
    symbolId,
  ]);

  if (state.cacheKey !== asset.cacheKey) {
    return { gltf: null, failed: false };
  }
  return { gltf: state.gltf, failed: state.failed };
}

function loadSymbolAssetGLTF(
  asset: ResolvedSymbol3DAsset,
  gl: THREE.WebGLRenderer,
): Promise<LoadedSymbolGLTF> {
  const cached = symbolAssetLoadCache.get(asset.cacheKey);
  if (cached?.status === "loaded") {
    return Promise.resolve(cached.gltf);
  }
  if (cached?.status === "loading") {
    return cached.promise;
  }

  let ktx2Loader: KTX2Loader | undefined;

  const loader = new GLTFLoader();
  if (asset.source === "userModel") {
    loader.setWithCredentials(true);
  }
  if (!sharedDracoLoader) {
    sharedDracoLoader = new DRACOLoader();
    sharedDracoLoader.setDecoderPath(DRACO_DECODER_PATH);
  }
  loader.setDRACOLoader(sharedDracoLoader);
  loader.setMeshoptDecoder(
    typeof MeshoptDecoder === "function" ? MeshoptDecoder() : MeshoptDecoder,
  );
  if (asset.compression?.textures === "ktx2") {
    ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath("/basis/");
    ktx2Loader.detectSupport(gl);
    loader.setKTX2Loader(ktx2Loader);
  }

  const promise = (
    loader.loadAsync(asset.url) as Promise<LoadedSymbolGLTF>
  ).then(
    (gltf) => {
      ktx2Loader?.dispose();
      symbolAssetLoadCache.set(asset.cacheKey, { status: "loaded", gltf });
      return gltf;
    },
    (error) => {
      ktx2Loader?.dispose();
      symbolAssetLoadCache.delete(asset.cacheKey);
      throw error;
    },
  );
  symbolAssetLoadCache.set(asset.cacheKey, { status: "loading", promise });
  return promise;
}

function trackSymbolAssetFallback(
  symbolId: string,
  asset: ResolvedSymbol3DAsset,
  error: unknown,
) {
  trackFloorplan3DMetric("symbol_3d_asset_fallback", {
    symbolId,
    source: asset.source,
    assetId: asset.id,
    url: asset.url,
    message: error instanceof Error ? error.message : String(error),
  });
}

export function createSymbolAssetMeshInstance(
  scene: THREE.Object3D,
  source: ResolvedSymbol3DAsset["source"],
  version?: string,
  yawOffset = 0,
  pitchOffset = 0,
  rollOffset = 0,
): { assetRoot: THREE.Group; meshBounds: THREE.Box3 } {
  const mesh = scene.clone(true);
  // v1 and v3 user models are delivered Y-up while the floorplanner scene is
  // Z-up. v2 and v4+ assets were backend-normalized and need no correction.
  if (
    source === "userModel" &&
    (version === "user-models-v1" || version === "user-models-v3")
  ) {
    mesh.rotation.x = Math.PI / 2;
  }
  if (pitchOffset !== 0) {
    mesh.rotation.x += pitchOffset;
  }
  if (rollOffset !== 0) {
    mesh.rotation.y += rollOffset;
  }
  if (yawOffset !== 0) {
    mesh.rotation.z += yawOffset;
  }
  const assetRoot = new THREE.Group();
  assetRoot.add(mesh);
  assetRoot.updateMatrixWorld(true);

  return {
    assetRoot,
    meshBounds: new THREE.Box3().setFromObject(assetRoot),
  };
}

export function getSymbolAssetOrientedBounds(
  meshBounds: THREE.Box3,
  rotation3D?: Symbol3DRotation,
): THREE.Box3 {
  const pitch = rotation3D?.pitch ?? 0;
  const roll = rotation3D?.roll ?? 0;
  const yaw = rotation3D?.yaw ?? 0;
  const rotationEpsilon = 1e-8;
  if (
    Math.abs(pitch) < rotationEpsilon &&
    Math.abs(roll) < rotationEpsilon &&
    Math.abs(yaw) < rotationEpsilon
  ) {
    return meshBounds;
  }

  const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(pitch, roll, yaw, "XYZ"),
  );
  const orientedBounds = new THREE.Box3();
  const corner = new THREE.Vector3();
  [
    meshBounds.min,
    meshBounds.max,
    new THREE.Vector3(meshBounds.min.x, meshBounds.min.y, meshBounds.max.z),
    new THREE.Vector3(meshBounds.min.x, meshBounds.max.y, meshBounds.min.z),
    new THREE.Vector3(meshBounds.min.x, meshBounds.max.y, meshBounds.max.z),
    new THREE.Vector3(meshBounds.max.x, meshBounds.min.y, meshBounds.min.z),
    new THREE.Vector3(meshBounds.max.x, meshBounds.min.y, meshBounds.max.z),
    new THREE.Vector3(meshBounds.max.x, meshBounds.max.y, meshBounds.min.z),
  ].forEach((point) => {
    corner.copy(point).applyMatrix4(rotationMatrix);
    orientedBounds.expandByPoint(corner);
  });
  return orientedBounds;
}

export function createSymbolAssetModelInstance(
  scene: THREE.Object3D,
  source: ResolvedSymbol3DAsset["source"],
  version?: string,
  yawOffset = 0,
  rotation3D?: Symbol3DRotation,
  pitchOffset = 0,
  rollOffset = 0,
): { model: THREE.Object3D; modelBounds: THREE.Box3 } {
  const { assetRoot, meshBounds } = createSymbolAssetMeshInstance(
    scene,
    source,
    version,
    yawOffset,
    pitchOffset,
    rollOffset,
  );
  if (!rotation3D) {
    return { model: assetRoot, modelBounds: meshBounds };
  }

  const root = new THREE.Group();
  root.rotation.set(
    rotation3D.pitch ?? 0,
    rotation3D.roll ?? 0,
    rotation3D.yaw ?? 0,
  );
  root.add(assetRoot);
  root.updateMatrixWorld(true);

  return {
    model: root,
    modelBounds: getSymbolAssetOrientedBounds(meshBounds, rotation3D),
  };
}

/**
 * Baseline opacity/transparency for a symbol-asset material.
 *
 * The 3D scene fade (`setSceneMaterialOpacity`) multiplies material.opacity in
 * place. If we clone after that wipe, `material.opacity` may already be ~0 and
 * must not be treated as the asset's intrinsic opacity — otherwise fillOpacity
 * 100% permanently sticks at the faded value until refresh.
 */
function resolveSymbolAssetMaterialBaseline(material: THREE.Material): {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
} {
  const transitionState = material.userData.transitionMaterialState as
    | {
        opacity?: unknown;
        transparent?: unknown;
        depthWrite?: unknown;
      }
    | undefined;
  if (
    transitionState &&
    typeof transitionState.opacity === "number" &&
    Number.isFinite(transitionState.opacity)
  ) {
    return {
      opacity: transitionState.opacity,
      transparent:
        typeof transitionState.transparent === "boolean"
          ? transitionState.transparent
          : material.transparent,
      depthWrite:
        typeof transitionState.depthWrite === "boolean"
          ? transitionState.depthWrite
          : material.depthWrite,
    };
  }
  return {
    opacity: material.opacity,
    transparent: material.transparent,
    depthWrite: material.depthWrite,
  };
}

export function prepareSymbolAssetMeshMaterial(
  mesh: THREE.Mesh,
  options: {
    fillColor?: number;
    opacity?: number;
    applyUnreadableUserModelFallback?: boolean;
  } = {},
) {
  const materials = Array.isArray(mesh.material)
    ? mesh.material.map(cloneSymbolAssetMaterial)
    : cloneSymbolAssetMaterial(mesh.material);
  mesh.material = materials;

  const materialList = Array.isArray(materials) ? materials : [materials];
  materialList.forEach((material) => {
    const originalOpacity =
      typeof material.userData.floorplannerSymbolAssetOriginalOpacity === "number"
        ? material.userData.floorplannerSymbolAssetOriginalOpacity
        : material.opacity;
    const originalTransparent =
      typeof material.userData.floorplannerSymbolAssetOriginalTransparent === "boolean"
        ? material.userData.floorplannerSymbolAssetOriginalTransparent
        : material.transparent;
    const originalDepthWrite =
      typeof material.userData.floorplannerSymbolAssetOriginalDepthWrite === "boolean"
        ? material.userData.floorplannerSymbolAssetOriginalDepthWrite
        : material.depthWrite;
    const objectOpacity = clampFillOpacity(options.opacity ?? 1);

    if (
      options.applyUnreadableUserModelFallback &&
      isUnreadableUntexturedBlackMaterial(material)
    ) {
      material.color.set(0x6f6a5d);
      if ("roughness" in material && typeof material.roughness === "number") {
        material.roughness = Math.max(material.roughness, 0.65);
      }
    }

    if (options.fillColor !== undefined && hasMaterialColor(material)) {
      material.color.set(options.fillColor);
    }

    material.opacity = originalOpacity * objectOpacity;
    material.transparent = originalTransparent || material.opacity < 1;
    material.depthWrite = material.opacity < 1 ? false : originalDepthWrite;
    // Drop scene-fade bookkeeping so the next setSceneMaterialOpacity pass
    // re-captures this fill-adjusted absolute opacity as its baseline.
    delete material.userData.transitionMaterialState;

    material.needsUpdate = true;
  });
}

function cloneSymbolAssetMaterial<T extends THREE.Material>(material: T): T {
  if (material.userData.floorplannerSymbolAssetMaterialClone) return material;
  const baseline = resolveSymbolAssetMaterialBaseline(material);
  const clone = material.clone();
  clone.opacity = baseline.opacity;
  clone.transparent = baseline.transparent;
  clone.depthWrite = baseline.depthWrite;
  delete clone.userData.transitionMaterialState;
  clone.userData = {
    ...clone.userData,
    floorplannerSymbolAssetMaterialClone: true,
    floorplannerSymbolAssetOriginalOpacity: baseline.opacity,
    floorplannerSymbolAssetOriginalTransparent: baseline.transparent,
    floorplannerSymbolAssetOriginalDepthWrite: baseline.depthWrite,
  };
  return clone as T;
}

function hasMaterialColor(
  material: THREE.Material,
): material is THREE.Material & { color: THREE.Color } {
  return (
    (material as THREE.Material & { color?: unknown }).color instanceof
    THREE.Color
  );
}

function isUnreadableUntexturedBlackMaterial(
  material: THREE.Material,
): material is THREE.MeshStandardMaterial {
  const candidate = material as THREE.MeshStandardMaterial;
  if (!(candidate.color instanceof THREE.Color)) return false;

  const maybeTextured = material as THREE.MeshStandardMaterial & {
    map?: THREE.Texture | null;
    emissiveMap?: THREE.Texture | null;
    aoMap?: THREE.Texture | null;
  };
  if (maybeTextured.map || maybeTextured.emissiveMap || maybeTextured.aoMap) {
    return false;
  }

  const maxChannel = Math.max(
    candidate.color.r,
    candidate.color.g,
    candidate.color.b,
  );
  return maxChannel < 0.04;
}

export default LivingRoomPrimitive3DMeshes;
